import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { DataPrepQuery } from 'dashboard-services/api/dataPrep';
import { DataStorageQuery } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';

import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

export default (() => {

  const useSearchSymbols = ({ query, access, collapse, symbolValuesExactMatch, withValuesOnly, getQueryBuilder, sortBy, shouldBeLimited, size = CommonService.getRequestSize(), shouldRefetch = true, provider, ...rest }) => (
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.searchSymbols(
        getQueryBuilder,
        {
          size,
          sortBy,
          collapse,
          query,
          symbolValuesExactMatch,
          withValuesOnly,
          provider,
          access
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      ...rest
    })
  )

  // Paginated by nextPageToken - initialPageParam / getNextPageParam come from dashboard-services and must not be overridden
  const useSearchSymbolsEnvs = ({ query, access, collapse, symbolValuesExactMatch, withValuesOnly, getQueryBuilder, sortBy, shouldBeLimited, size = CommonService.getRequestSize(), shouldRefetch = true, ...rest }) => (
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.searchSymbolsEnvs(
        getQueryBuilder,
        {
          size,
          sortBy,
          collapse,
          query,
          symbolValuesExactMatch,
          withValuesOnly,
          access
        }
      ),
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      ...rest
    })
  )

  const useGetDataPrepById = ({ getQueryBuilder, id, withMetadata = false, resolvePattern = false, enabled = true, ...rest }) => 
    useQuery({
      ...DataPrepQuery.QUERIES.getById(
        getQueryBuilder,
        {
          id,
          withMetadata,
          resolvePattern
        }
      ),
      enabled: !!id && enabled,
      ...rest
    })

  const useGetDataPrepByName = ({ getQueryBuilder, name, groupName, version, withMetadata = false, resolvePattern = false, enabled = true, ...rest }) => 
    useQuery({
      ...DataPrepQuery.QUERIES.getByName(
        getQueryBuilder,
        {
          name,
          groupName,
          version,
          withMetadata,
          resolvePattern
        }
      ),
      enabled: !!name && !!groupName && enabled,
      ...rest
    })

  const useGetDataPrepByIdMutation = ({ getQueryBuilder, id, withMetadata = false, resolvePattern = false }) => 
    useMutation({
      ...DataPrepQuery.QUERIES.getByIdAsMutation(
        getQueryBuilder,
        {
          id,
          withMetadata,
          resolvePattern
        }
      )
    })

  const useGetDataPrepByNameMutation = ({ getQueryBuilder, name, groupName, version, resolvePattern, withMetadata, ...rest }) =>
    useMutation({
      ...DataPrepQuery.QUERIES.getByNameAsMutation(
        getQueryBuilder,
        {
          name,
          groupName,
          version,
          resolvePattern,
          withMetadata
        }
      ),
      ...rest
    })

  const useSearchDataPreps = ({ getQueryBuilder, query, size = CommonService.getRequestSize(), latest = true, shouldBeLimited, shouldRefetch = true, enabled = true, ...rest }) =>
    useInfiniteQuery({
      ...DataPrepQuery.QUERIES.search(
        getQueryBuilder,
        {
          query,
          size,
          latest
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      enabled,
      ...rest
    })

  const useSearchDataPrepsMutation = ({ getQueryBuilder, query, size = CommonService.getRequestSize(), latest = true, ...rest }) =>
    useMutation({
      ...DataPrepQuery.QUERIES.searchMutation(
        getQueryBuilder,
        {
          query,
          size,
          from: 0,
          latest
        }
      ),
      ...rest
    })

  const useSearchSymbolKeys = ({ getQueryBuilder, groupName, query, symbols, size, sort, metadata, metadatas, useEs, omitRequiredKeys, provider, enabled = true, ...rest }) =>
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.getSymbolKeys(
        getQueryBuilder,
        {
          groupName,
          query, 
          symbols,
          size,
          sort,
          metadata,
          metadatas,
          useEs,
          omitRequiredKeys,
          provider
        }
      ),
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam,
      enabled,
      ...rest
    })

  
  const useFavoriteDataPreps = ({ favoriteDataPreps, getQueryBuilder, shouldBeLimited, userName }) => {
    const queries = useMemo(() => favoriteDataPreps.map(dp => ({
            ...DataPrepQuery.QUERIES.getVersionsById(
              getQueryBuilder,
              {
                id: dp.id,
                size: 1,
                favorite: true
              },
            ),
            enabled: !!userName && !!dp.id,
            refetchInterval: () => shouldBeLimited() ? undefined : 10000,
            refetchOnWindowFocus: false,
            select: response => response.items?.[0]
          })), [favoriteDataPreps, getQueryBuilder, shouldBeLimited, userName]),
          combine = useCallback(results => ({
            data: results.map(result => result.data).filter(dp => dp?.id !== undefined).map(g => ReduxService.updateObject(g, { favorite: true })),
            isPending: results.some(result => result.isPending),
          }), [])
    return useQueries({
      queries,
      combine
    })
  }

  const invalidateGets = queryClient => 
    queryClient.invalidateQueries(
      { queryKey: DataPrepQuery.KEYS.ALL }
    )

  const useRunDataPrep = ({ getQueryBuilder, id, queryClient, onSuccess, ...rest }) =>
    useMutation({
      ...DataPrepQuery.QUERIES.execute(
        getQueryBuilder,
        {
          id
        }
      ),
      onSuccess: () => {
        invalidateGets(queryClient)
        onSuccess?.()
      },
      ...rest
    })

  const useCreateDataPrep = ({ getQueryBuilder, dataPrep, queryClient, onSuccess, ...rest }) =>
    useMutation({
      ...DataPrepQuery.QUERIES.create(
        getQueryBuilder,
        {
          dataPrep
        }
      ),
      onSuccess: () => {
        invalidateGets(queryClient)
        onSuccess?.()
      },
      ...rest
    })

  const useUpdateDataPrep = ({ getQueryBuilder, id, dataPrep, queryClient, onSuccess, ...rest }) =>
    useMutation({
      ...DataPrepQuery.QUERIES.update(
        getQueryBuilder,
        {
          id,
          dataPrep
        }
      ),
      onSuccess: () => {
        invalidateGets(queryClient)
        onSuccess?.()
      },
      ...rest
    })

  const useGetVersionsById = ({ getQueryBuilder, id, size = CommonService.getRequestSize(), shouldBeLimited, shouldRefetch = true, enabled = true, ...rest }) => 
    useInfiniteQuery({
      ...DataPrepQuery.QUERIES.getVersionsById(
        getQueryBuilder,
        {
          id,
          size
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      enabled: !!id && enabled,
      ...rest
    })

  const useGetVersionsByName = ({ getQueryBuilder, name, size = CommonService.getRequestSize(), shouldBeLimited, shouldRefetch = true, enabled = true, ...rest }) => 
    useInfiniteQuery({
      ...DataPrepQuery.QUERIES.getVersionsByName(
        getQueryBuilder,
        {
          name,
          size
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      enabled: !!name && enabled,
      ...rest
    })

  const useDeleteDataPrep = ({ getQueryBuilder, id, queryClient, onSuccess, ...rest }) =>
    useMutation({
      ...DataPrepQuery.QUERIES.delete(
        getQueryBuilder,
        {
          id
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: DataPrepQuery.KEYS.SEARCH() })
        onSuccess?.()
      },
      ...rest
    })

  const useGetListOfSymbolsInFile = ({ getQueryBuilder, fileId, enabled = true, ...rest }) =>
    useQuery({
      ...DataStorageQuery.QUERIES.getListOfSymbolsInFile(
        getQueryBuilder,
        {
          fileId
        }
      ),
      enabled: !!fileId && enabled,
      ...rest
    })

  const useGetData = ({ getQueryBuilder, body, size, order, sortSymbols, symbolSize, csv, omitNulls, absoluteSize, ...rest }) =>
    useQuery({
      ...DataStorageQuery.QUERIES.getData(
        getQueryBuilder,
        {
          body,
          size,
          order,
          sortSymbols,
          symbolSize,
          csv,
          omitNulls,
          absoluteSize
        }
      ),
      gcTime: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...rest
    })

  const useGetDataMutation = ({ getQueryBuilder, body, size, order, sortSymbols, symbolSize, csv, omitNulls, absoluteSize, ...rest }) =>
    useMutation({
      ...DataStorageQuery.QUERIES.getDataMutation(
        getQueryBuilder,
        {
          body,
          size,
          order,
          sortSymbols,
          symbolSize,
          csv,
          omitNulls,
          absoluteSize
        }
      ),
      ...rest
    })

  const useGetDataFromSqlMutation = ({ getQueryBuilder, ...rest }) =>
    useMutation({
      ...DataStorageQuery.QUERIES.getDataFromSQL(getQueryBuilder),
      ...rest
    })

  const useGetStreamData = ({ getQueryBuilder, body, size, order, ...rest }) =>
    useQuery({
      ...DataStorageQuery.QUERIES.getStreamData(
        getQueryBuilder,
        { 
          body,
          size,
          order
        }
      ),
      gcTime: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...rest
    })

  const useGetHolidayCalendars = ({ query, size, getQueryBuilder, ...rest }) => 
    useInfiniteQuery({
      ...DataStorageQuery.QUERIES.getHolidayCalendars(
        getQueryBuilder,
        {
          query: query ? `*${query}*` : "*",
          size
        }
      ),
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam,
      ...rest
    })

  const useSaveDataPrep = ({ setErrors } = {}) => {
    const dispatch = useDispatch(),
          getQueryBuilderSave = useCallback(() => {
            let builder = 
              new Query.Builder(dispatch(getApiConfig()))
                .withShouldTimeOut(false)
            if(setErrors !== undefined) {
              builder = builder.withOnError(({ response }) => {
                const errors = response.validationErrors
                const formErrors = (errors || []).filter((e = {}) => e.field === "name" || e.field === "groupName")
                const differentErrors = (errors || []).filter((e = {}) => e.field !== "name" && e.field !== "groupName")
                if(differentErrors && differentErrors.length > 0) {
                  differentErrors.forEach(e => dispatch(globalActions.getMessageStore()).error(e.message))
                }
                setErrors(formErrors)
              })
            }
            return builder;
          }, [dispatch, setErrors]),
          queryClient = useQueryClient(),
          { mutateAsync: createCall } = useCreateDataPrep({ getQueryBuilder: getQueryBuilderSave, queryClient }),
          { mutateAsync: updateCall } = useUpdateDataPrep({ getQueryBuilder: getQueryBuilderSave, queryClient })

    return { updateCall, createCall }
  }

  const useRemoveSymbol = ({ getQueryBuilder, checkForTsData, groupName, symbols, queryClient, onSuccess, ...rest }) =>
    useMutation({
      ...DataStorageQuery.QUERIES.removeSymbol(
        getQueryBuilder,
        {
          checkForTsData,
          groupName,
          symbols
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries(
          { queryKey: DataStorageQuery.KEYS.SEARCH_SYMBOLS() }
        )
        onSuccess?.()
      },
      ...rest
    })


  return { 
    useSearchSymbols,
    useSearchSymbolsEnvs,
    useGetDataPrepById,
    useGetDataPrepByIdMutation,
    useSearchDataPreps,
    useSearchSymbolKeys,
    useFavoriteDataPreps,
    useRunDataPrep,
    useCreateDataPrep,
    useUpdateDataPrep,
    useGetVersionsById,
    useGetVersionsByName,
    useDeleteDataPrep,
    useSearchDataPrepsMutation,
    useGetDataPrepByNameMutation,
    useGetDataPrepByName,
    useGetListOfSymbolsInFile,
    useGetData,
    useGetDataMutation,
    useGetDataFromSqlMutation,
    useGetStreamData,
    useGetHolidayCalendars,
    useSaveDataPrep,
    useRemoveSymbol
  }
})()
